import { twMerge } from "tailwind-merge";
import type { DeviceState } from "../../../types/DeviceStates";
import type { GridState } from "../../../types/GridStates";
import type { FieldFormat } from "../../../utils/formatting/types";
import { DeviceStateBadge } from "../../DeviceStateBadge";
import { DeviceStateWithMetric } from "../../DeviceStateWithMetric";
import type { IconName } from "../../Icon";
import { Skeleton } from "../../Skeleton";
import type { CellComponentProps } from "../DataTable.types";
import { getCellContainerClasses, resolveValue } from "./cellUtils";

export interface DeviceStateCellProps<T = any> extends Omit<CellComponentProps<T>, "value"> {
  /** The device state value */
  value?: DeviceState | null | undefined;

  /** Optional metric to display alongside state */
  metric?: number | null | ((row: T) => number | null);

  /** Formatter for the metric - uses FieldFormat! */
  metricFormatter?: FieldFormat;

  /** Optional secondary state (e.g., grid status) */
  secondaryState?: DeviceState | GridState | ((row: T) => DeviceState | GridState | null);

  /** Badge size */
  size?: "sm" | "md" | "lg";

  /** Badge shape */
  shape?: "pill" | "rounded" | "square";

  /** Custom label override */
  label?: string | ((state: DeviceState, row: T) => string);

  /** Optional icon */
  icon?: IconName;

  /** Fixed width for column alignment (defaults to true for tables) */
  fixedWidth?: boolean;

  /** Alignment */
  align?: "left" | "center" | "right";

  /** Compact mode - shows secondary state as icon only (arrow up/down for grid states) */
  compact?: boolean;

  /** Additional classes */
  className?: string;
}

/**
 * DeviceStateCell
 *
 * DataTable cell component for displaying device states with optional metrics.
 * Uses DeviceStateWithMetric internally when metric is provided,
 * or DeviceStateBadge when no metric.
 *
 * @example
 * ```tsx
 * // In DataTable column definition
 * {
 *   id: "state",
 *   label: "Status",
 *   cell: DeviceStateCell,
 *   cellProps: {
 *     metric: (row) => row.chargePercentage,
 *     metricFormatter: { type: "number", suffix: "%" }
 *   }
 * }
 *
 * // With secondary state
 * {
 *   id: "state",
 *   label: "Status",
 *   cell: DeviceStateCell,
 *   cellProps: {
 *     metric: (row) => row.power,
 *     metricFormatter: { type: "power", unit: "kW" },
 *     secondaryState: (row) => row.gridState
 *   }
 * }
 * ```
 */
export function DeviceStateCell<T = any>({
  value,
  row,
  context,
  metric,
  metricFormatter,
  secondaryState,
  size = "sm",
  shape = "pill",
  label,
  icon,
  fixedWidth = true,
  align = "left",
  compact = false,
  className,
}: DeviceStateCellProps<T>) {
  const { isLoading } = context;

  if (isLoading) {
    const shapeClasses = {
      pill: "rounded-full",
      rounded: "rounded-lg",
      square: "rounded-sm",
    };
    const badgeRadius = shapeClasses[shape];

    // Show full layout if metric is expected, otherwise just a badge
    const hasMetric = metric != null || metricFormatter != null;

    return (
      <div className="flex items-center gap-2">
        {hasMetric ? (
          <>
            <Skeleton className="h-4 w-12" />
            <Skeleton className={`h-6 w-24 ${badgeRadius}`} />
            <Skeleton className={`h-6 w-20 ${badgeRadius}`} />
          </>
        ) : (
          <Skeleton className={`h-6 w-24 ${badgeRadius}`} />
        )}
      </div>
    );
  }

  if (!value) {
    return (
      <div className={getCellContainerClasses(align, className)}>
        <span className="text-text-muted">—</span>
      </div>
    );
  }

  // Resolve metric value
  const metricValue = resolveValue(metric, row);

  // Resolve secondary state
  const secondaryStateValue = resolveValue(secondaryState, row);

  // Resolve label
  const labelValue = typeof label === "function" ? label(value, row) : label;

  const containerClasses = getCellContainerClasses(align, className);

  // If metric is provided, use DeviceStateWithMetric
  if (metricValue != null || metricFormatter) {
    return (
      <div className={twMerge(containerClasses)}>
        <DeviceStateWithMetric
          state={value}
          stateLabel={labelValue}
          metric={metricValue}
          metricFormatter={metricFormatter}
          secondaryState={secondaryStateValue || undefined}
          size={size}
          shape={shape}
          icon={icon}
          fixedWidth={fixedWidth}
          secondaryStateIconOnly={compact}
          layout="horizontal"
        />
      </div>
    );
  }

  // Otherwise use simple DeviceStateBadge
  return (
    <div className={containerClasses}>
      <DeviceStateBadge
        state={value}
        label={labelValue}
        icon={icon}
        size={size}
        shape={shape}
        fixedWidth={fixedWidth}
      />
    </div>
  );
}
